// components/data-quality/StatusProgressionChart.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListAlt, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const StatusProgressionChart = ({
  statusData,
  statusCompletion,
  totalBookings,
  expectedStatusProgression,
  statusDescriptions,
}) => {
  const [showInfo, setShowInfo] = useState(false);

  const progressionChartData = {
    labels: ["Valid Progression", "Invalid Progression"],
    datasets: [
      {
        label: "Bookings Count",
        data: [statusData.validProgression, statusData.invalidProgression],
        backgroundColor: [
          "rgba(34, 197, 94, 0.8)", // Green
          "rgba(239, 68, 68, 0.8)", // Red
        ],
        borderColor: ["rgba(34, 197, 94, 1)", "rgba(239, 68, 68, 1)"],
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 100,
      },
    ],
  };

  const completionChartData = {
    labels: ["Complete", "Incomplete"],
    datasets: [
      {
        label: "Bookings Count",
        data: [statusCompletion.complete, statusCompletion.incomplete],
        backgroundColor: [
          "rgba(59, 130, 246, 0.8)", // Blue
          "rgba(249, 115, 22, 0.8)", // Orange
        ],
        borderColor: ["rgba(59, 130, 246, 1)", "rgba(249, 115, 22, 1)"],
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 100,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = ((value / totalBookings) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between flex-shrink-0">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faListAlt} className="text-blue-600 mr-2" />
          Status Progression Analysis
        </h3>
        <button
          className="text-sm text-gray-500 flex items-center"
          onClick={() => setShowInfo(!showInfo)}
        >
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          <span>{showInfo ? "Hide Info" : "Show Info"}</span>
        </button>
      </div>

      {showInfo && (
        <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-lg text-sm flex-shrink-0">
          <div className="font-medium mb-1">Expected Status Progression:</div>
          <ol className="list-decimal pl-5 mb-2">
            {expectedStatusProgression.map((status, index) => (
              <li key={status}>
                <strong>{status}</strong>: {statusDescriptions[status]}
              </li>
            ))}
          </ol>
          <div className="text-xs mt-2">
            Valid progression means the status updates follow this sequence
            without skipping steps or going backwards.
          </div>
        </div>
      )}

      {/* Add a wrapper with a fixed height and make it flex-grow */}
      <div
        className="flex-grow flex-shrink min-h-0 h-0"
        style={{ flex: "1 1 auto" }}
      >
        <div className="grid grid-cols-2 gap-4 h-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-col">
            <div className="font-medium text-sm text-gray-700 mb-2 text-center flex-shrink-0">
              Progression Validity
            </div>
            <div className="flex-grow min-h-0 relative">
              <Bar data={progressionChartData} options={chartOptions} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-col">
            <div className="font-medium text-sm text-gray-700 mb-2 text-center flex-shrink-0">
              Status Completion
            </div>
            <div className="flex-grow min-h-0 relative">
              <Bar data={completionChartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 text-xs text-gray-500 flex-shrink-0">
        Complete: Bookings with all {expectedStatusProgression.length} expected
        status updates. Valid: Bookings with status updates in the correct
        sequence.
      </div>
    </div>
  );
};

export default StatusProgressionChart;
